import "./receipts.scss";
import {
    createContext,
    type CSSProperties,
    type FC,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    ReceiptLocalToServerMap,
    type ReceiptPaddingType,
    type ReceiptServerTypeUnion,
    type ReceiptTypeUnion,
} from "../../Data/_Bases/BaseReceiptRepository";
import { determineDataSource } from "../../Data/DataStores/DataSource";
import { ReceiptErrorBoundary } from "../../Routes/Settings/Receipts/ReceiptErrorBoundary";
import { getReceiptDefaultColumns } from "../../Routes/Settings/Receipts/ReceiptModify";
import { currency } from "../../Utilities/Generic/Numbers";
import FontAwesomeLight from "../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { isTrainingMode } from "../../Utilities/Platform/Options/Options";
import { usePrint } from "../../Utilities/UI/PrintHelper";
import { DragDropComponentErrorContext } from "../Forms/DragDrop/DragDropComponent";
import { DragDropRenderer } from "../Forms/DragDrop/DragDropRenderer";
import { type ComponentType, type DragDropItemMap, isColumnComponentType } from "../Forms/DragDrop/DragDropTypes";
import { createStyleFromPadding } from "../Forms/PaddingInput";
import { ReceiptAccountTableComponent } from "./Components/ReceiptAccountTableComponent";
import { ReceiptBarcodeComponent } from "./Components/ReceiptBarcodeComponent";
import { ReceiptEFTPOSComponent } from "./Components/ReceiptEFTPOSComponent";
import { ReceiptGiftCardsComponent } from "./Components/ReceiptGiftCardsComponent";
import { ReceiptHeadingComponent } from "./Components/ReceiptHeadingComponent";
import { ReceiptInvoiceTableComponent } from "./Components/ReceiptInvoiceTableComponent";
import { isReceiptLogoComponentValue, ReceiptLogoComponent } from "./Components/ReceiptLogoComponent";
import { ReceiptLoyaltyTableComponent } from "./Components/ReceiptLoyaltyTableComponent";
import { ReceiptPaymentTableComponent } from "./Components/ReceiptPaymentTableComponent";
import { ReceiptProductTableComponent } from "./Components/ReceiptProductTableComponent";
import { ReceiptSurchargeComponent } from "./Components/ReceiptSurchargeComponent";
import { isTaxIndicatorComponent, ReceiptTaxIndicatorComponent } from "./Components/ReceiptTaxIndicatorComponent";
import { ReceiptTaxTableComponent } from "./Components/ReceiptTaxTableComponent";
import { ReceiptTotalTableComponent } from "./Components/ReceiptTotalTableComponent";
import TextReceipt from "./TextReceipt";

interface Props {
    type: ReceiptTypeUnion;
    format: Array<ComponentType>;
    data: Record<string, unknown>;
    accountReceipt?: boolean;
    onReady?: () => void;
    classes?: Array<string>;
    width?: number;
    padding: null | ReceiptPaddingType;
    attachment?: boolean;
}

type TaxIndicatorMap = Record<string, string>;

export function getTaxIndicators(components: Array<ComponentType>): TaxIndicatorMap {
    let taxIndicators: TaxIndicatorMap = {};

    for(let i = 0, l = components.length; i < l; i++) {
        const component = components[i];

        if(isTaxIndicatorComponent(component)) {
            taxIndicators[component.value.tax] = component.value.indicator;
        } else if(isColumnComponentType(component)) {
            for(let j = 0; j < component.value.length; j++) {
                const column = component.value[j];

                taxIndicators = {
                    ...taxIndicators,
                    ...getTaxIndicators(column.components),
                };
            }
        }
    }

    return taxIndicators;
}

/**
 * Recursively check the receipt for a surcharge component
 */
function hasSurchargeComponent(components: Array<ComponentType>): boolean {
    for(const component of components) {
        if(component.type === "surcharge") {
            return true;
        } else if(isColumnComponentType(component)) {
            // Check the columns for the surcharge component
            for(const column of component.value) {
                if(hasSurchargeComponent(column.components)) {
                    return true;
                }
            }
        }
    }

    return false;
}

export const ReceiptTypeContext = createContext<ReceiptServerTypeUnion>("A4");
export const ReceiptAccountContext = createContext(false);

export const Receipt: FC<Props> = ({
    type,
    format,
    data,
    accountReceipt,
    onReady,
    width,
    padding,
    attachment,
    classes: classProps,
}) => {
    const items = useMemo<DragDropItemMap>(() => {
        const results: DragDropItemMap = {};

        if(!accountReceipt) {
            results.heading = { component: ReceiptHeadingComponent };
        }

        if(ReceiptLocalToServerMap[type] !== "TEXT_ONLY") {
            results.barcode = { component: ReceiptBarcodeComponent };
            results.logo = { component: ReceiptLogoComponent };
        }

        results.payment = { component: ReceiptPaymentTableComponent };
        results.total = { component: ReceiptTotalTableComponent };
        results.eftpos = { component: ReceiptEFTPOSComponent };

        if(accountReceipt) {
            results.invoices = { component: ReceiptInvoiceTableComponent };
        } else {
            results.product = { component: ReceiptProductTableComponent };
            results.tax = { component: ReceiptTaxTableComponent };
            results["tax_indicator"] = { component: ReceiptTaxIndicatorComponent };
            results.surcharge = { component: ReceiptSurchargeComponent };
            results.loyalty = { component: ReceiptLoyaltyTableComponent };
            results.account = { component: ReceiptAccountTableComponent };
            results["gift_cards"] = { component: ReceiptGiftCardsComponent };
        }

        return results;
    }, [ type, accountReceipt ]);

    const [ taxRates, setTaxRates ] = useState<Record<string, string>>({});

    useEffect(() => {
        determineDataSource()
            .then(source => source.taxRates.all())
            .then(taxRates => {
                const map: Record<string, string> = {};

                for(const taxRate of taxRates) {
                    map[taxRate.uuid] = taxRate.name;
                }

                setTaxRates(map);
            })
            .catch(err => {
                console.error("Failed to load tax rates", err);
            });
    }, []);

    const indicators = useMemo(() => {
        return getTaxIndicators(format);
    }, [ format ]);

    const components = useMemo<Array<ComponentType>>(() => {
        if(accountReceipt) {
            return format;
        }

        if(typeof data.total !== "object") {
            return format;
        }

        if(data.total === null) {
            return format;
        }

        if("surcharge" in data.total && (
            typeof data.total.surcharge !== "number" ||
            data.total.surcharge === 0
        )) {
            return format;
        }

        if(!hasSurchargeComponent(format)) {
            return [
                ...format,
                {
                    type : "surcharge",
                    value: {
                        columns: getReceiptDefaultColumns("surcharge", ReceiptLocalToServerMap[type], false),
                    },
                    style: {
                        color     : "#000000",
                        background: "#FFFFFF",
                        fontSize  : "12",
                        fontWeight: "normal",
                    },
                },
            ];
        }

        return format;
    }, [ format, accountReceipt, data.total, type ]);

    const formattedData = useMemo(() => {
        const tax: Array<{ id: string; tax: string; total: string; }> = [];

        if(Array.isArray(data.tax)) {
            tax.push(...data.tax);
        }

        // Check if all the required tax rates are met
        const required = { ...indicators };

        for(let i = 0, l = tax.length; i < l; i++) {
            if(required[tax[i].id]) {
                delete required[tax[i].id];
            }
        }

        // Add remaining required tax rates to data
        const remaining = Object.keys(required);

        for(let i = 0, l = remaining.length; i < l; i++) {
            if(!taxRates[remaining[i]]) {
                continue;
            }

            tax.push({
                id   : remaining[i],
                tax  : taxRates[remaining[i]],
                total: currency(0),
            });
        }

        let status = data.status;

        if(isTrainingMode()) {
            status = "training";
        }

        return {
            ...data,
            tax,
            status,
            indicators,
        };
    }, [ data, indicators, taxRates ]);

    const getComponentImage = useCallback((component: ComponentType): Record<string, number> => {
        if(component.type !== "logo") {
            return {};
        }

        if(typeof data.logo === "string") {
            return {
                [data.logo]: 1,
            };
        }

        if(isReceiptLogoComponentValue(component.value)) {
            const fallback = component.value.fallback;

            if(typeof fallback === "string") {
                return {
                    [fallback]: 1,
                };
            }
        }

        return {};
    }, [ data ]);

    let unsupported: undefined | ReactNode;

    if(type === "a4" && !attachment) {
        unsupported = (
            <div className="text-center">
                <div className="text-6xl text-yellow-500 mb-2">
                    <FontAwesomeLight icon="file" />
                </div>
                <span className="text-lg font-bold">A4</span>
                <div>Receipt preview unsupported</div>
            </div>
        );
    }

    let content;

    if(type === "receipt-text") {
        content = (
            <TextReceipt
                components={components}
                data={data}
                width={width as number}
                onReady={onReady}
            />
        );
    } else {
        let training: ReactNode = null;

        if(isTrainingMode()) {
            training = (
                <div
                    style={{
                        width    : "100%",
                        textAlign: "center",
                    }}
                >
                    TRAINING MODE
                </div>
            );
        }

        let style: undefined | CSSProperties;

        if(padding) {
            style = createStyleFromPadding(padding);
        }

        content = (
            <>
                <div style={style}>
                    <DragDropRenderer
                        getComponentImages={getComponentImage}
                        components={components}
                        items={items}
                        data={formattedData}
                        onReady={onReady}
                    />
                    {training}
                </div>
            </>
        );
    }

    const classes = [ `notranslate receipt ${type}` ];

    if(Array.isArray(classProps)) {
        classes.push(...classProps);
    }

    const receipt = (
        <div className={classes.join(" ")}>
            {content}
        </div>
    );

    const error = (
        <div
            className={
                "w-full h-full border border-dashed border-gray-500 text-gray-500 flex flex-col justify-center " +
                "items-center p-4 print:hidden"
            }
        >
            <div className="text-3xl text-red-400">
                <FontAwesomeLight icon="exclamation-triangle" />
            </div>
            <div className="text-center">Component in unexpected state. Receipt will print incorrectly</div>
        </div>
    );

    const toPrint = usePrint(receipt);

    return (
        <ReceiptTypeContext.Provider value={ReceiptLocalToServerMap[type]}>
            <ReceiptAccountContext.Provider value={!!accountReceipt}>
                <DragDropComponentErrorContext.Provider value={error}>
                    <ReceiptErrorBoundary type={ReceiptLocalToServerMap[type]}>
                        {typeof unsupported === "undefined" ? receipt : unsupported}
                    </ReceiptErrorBoundary>
                    {toPrint}
                </DragDropComponentErrorContext.Provider>
            </ReceiptAccountContext.Provider>
        </ReceiptTypeContext.Provider>
    );
};
